// La pantalla de entrada de Tienda online: los productos de la tienda conectada, leídos en vivo
// (ADR-2026-10-29-tienda-online-refleja-la-tienda). Reemplaza a la lista de lanzamientos.
//
// Una pestaña por tienda conectada; arranca en la primera que se conectó (o en la última que se
// miró). Tocar un producto abre su ficha: Tiendanube, la de siempre (en el lanzamiento invisible de la
// tienda); Mercado Libre, un panel con lo que hoy se puede cambiar de una publicación.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Check,
  ChevronRight,
  ExternalLink,
  Images,
  Loader2,
  ArrowDownToLine,
  Plus,
  RefreshCw,
  Sparkles,
  RotateCcw,
  Search,
  SlidersHorizontal,
  ListChecks,
  X,
} from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useBrandProfile } from "../../hooks/useBrandProfile";
import { useChatAtlas } from "../../chat-atlas";
import { prepararAdjunto } from "../../chat-atlas/ui/prepararAdjunto";
import { useMercadoLibreDisponible } from "../../hooks/useMercadoLibreDisponible";
import { abrirVentanaOAuth, connectTnEnVentana } from "../../lib/tiendaNube";
import { traerCatalogoAlConectar } from "../../lib/traerCatalogoAlConectar";
import {
  abrirVentanaDeMercadoLibre,
  conectarMercadoLibreEnVentana,
  ErrorDeMercadoLibre,
} from "../../lib/mercadoLibreConexion";
import { MercadoLibreFicha } from "../../components/MercadoLibreFicha";
import { TiendaNubeIcon } from "../../components/TiendaNubeIcon";
import { getPlatformLogo } from "../../components/PlatformLogos";
import { FiltroDeCategoria } from "../../components/FiltroDeCategoria";
import { SheetLateral } from "../../components/SheetLateral";
import { partesDelCamino } from "./arbolDeCategorias";
import type { CategoriaDeTienda } from "./lanzamientosApi";
import { SIN_CATEGORIA } from "../../lib/filtroPorCategoria";
import { PanelDeCategorias } from "./Categorias";
import { useCategorias } from "./useCategorias";
import { escucharCambiosDeLanzamiento } from "./cambioDeLanzamiento";
import {
  sincronizarLaTienda,
  type ResultadoDeSincronizar,
} from "./sincronizarTienda";
import {
  aplicarEdicionMasiva,
  hayEdicion,
  type EdicionMasiva,
} from "./edicionMasiva";
import { VISIBILIDADES, type Visibilidad } from "./camposDeLaTienda";
import {
  useHayVideoDePresentacion,
  VideoDePresentacion,
} from "./VideoDePresentacion";
import type { DesdeLaLista } from "./ExportarImportar";
import { HaloDeMarca, usePaletaDeMarca } from "../../components/HaloDeMarca";
import { enEnsayo } from "../../lib/ensayoPrimeraVez";
import { boton } from "./botones";
import { useMetricasVisibles } from "./metricas/comunes";
import { ResumenDeMetricas } from "./metricas/ResumenDeMetricas";
import {
  abrirProductoDeTiendanube,
  crearProductoEnBlanco,
  cuantosFiltros,
  type FiltrosDeLaTienda,
  lanzamientoDeLaTienda,
  sinSincronizarEnTiendanube,
  ErrorDeLaTienda,
  fetchProductosDeLaTienda,
  fetchTiendasConectadas,
  textoDeLoQueFalta,
  tiendaInicial,
  varianteDeMercadoLibre,
  type PlataformaDeTienda,
  type ProductoDeLaTienda,
  type TiendaConectada,
} from "./productosDeLaTienda";

const NOMBRE: Record<PlataformaDeTienda, string> = {
  tiendanube: "Tiendanube",
  mercadolibre: "Mercado Libre",
};
/** La última tienda que se miró, por marca: volver a la sección no la cambia. */
const claveTienda = (brandId: string | null) =>
  `atlas:tienda-online:tienda:${brandId ?? "sin-marca"}`;

function leerRecordada(brandId: string | null): string | null {
  try {
    return localStorage.getItem(claveTienda(brandId));
  } catch {
    return null;
  }
}

function recordar(brandId: string | null, plataforma: PlataformaDeTienda) {
  try {
    localStorage.setItem(claveTienda(brandId), plataforma);
  } catch {
    /* sin almacenamiento: arranca en la primera */
  }
}

export function formatoPrecio(n: number | null, moneda: string | null): string {
  if (n === null) return "Sin precio";
  try {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: moneda || "ARS",
      maximumFractionDigits: Number.isInteger(n) ? 0 : 2,
    }).format(n);
  } catch {
    return `$ ${n.toLocaleString("es-AR")}`;
  }
}

function textoDeStock(stock: number | null): {
  texto: string;
  agotado: boolean;
} {
  if (stock === null) return { texto: "Stock sin límite", agotado: false };
  if (stock <= 0) return { texto: "Sin stock", agotado: true };
  return { texto: `${stock} en stock`, agotado: false };
}

const ESTADO: Record<
  ProductoDeLaTienda["estado"],
  { texto: string; punto: string } | null
> = {
  publicado: null,
  oculto: { texto: "Oculto", punto: "bg-neutral-400" },
  pausado: { texto: "Pausado", punto: "bg-amber-400" },
};

type Lectura =
  | { estado: "cargando" }
  | { estado: "error"; mensaje: string; codigo: string }
  | {
      estado: "listo";
      productos: ProductoDeLaTienda[];
      hayMas: boolean;
      total: number | null;
      cargandoMas: boolean;
    };

export default function TiendaOnline() {
  const navigate = useNavigate();
  const { profile, currentBrandId } = useBrandProfile();
  const companyId = profile?.id_company ?? null;
  const [ensayo] = useState(enEnsayo);
  const metricasVisibles = useMetricasVisibles();
  const ml = useMercadoLibreDisponible(currentBrandId);
  // El halo de Mi marca, con los colores de la marca activa.
  const paleta = usePaletaDeMarca(currentBrandId);
  const escritorio = useEsEscritorio();

  // ── Las tiendas ───────────────────────────────────────────────────────────
  const [tiendas, setTiendas] = useState<TiendaConectada[] | null>(null);
  const [errorTiendas, setErrorTiendas] = useState<string | null>(null);
  const [actual, setActual] = useState<PlataformaDeTienda | null>(null);
  const [vueltaTiendas, setVueltaTiendas] = useState(0);

  useEffect(() => {
    if (!companyId) return;
    if (ensayo) {
      setTiendas([]);
      return;
    }
    // Hasta saber si Mercado Libre se le muestra a esta empresa: si no, su pestaña aparecía un segundo después.
    if (!ml.cargado) return;
    let vivo = true;
    setErrorTiendas(null);
    fetchTiendasConectadas(currentBrandId)
      .then((t) => {
        if (!vivo) return;
        // Mercado Libre sólo si el backend lo tiene prendido (con las credenciales de la app; en producción, sí).
        const visibles = t.filter(
          (x) => x.plataforma !== "mercadolibre" || ml.disponible,
        );
        setTiendas(visibles);
        setActual((prev) =>
          prev && visibles.some((x) => x.plataforma === prev)
            ? prev
            : tiendaInicial(visibles, leerRecordada(currentBrandId)),
        );
      })
      .catch((e: unknown) => {
        if (vivo)
          setErrorTiendas(
            e instanceof Error ? e.message : "No pudimos leer tus tiendas.",
          );
      });
    return () => {
      vivo = false;
    };
  }, [
    companyId,
    currentBrandId,
    ensayo,
    ml.cargado,
    ml.disponible,
    vueltaTiendas,
  ]);

  const elegirTienda = (p: PlataformaDeTienda) => {
    setActual(p);
    recordar(currentBrandId, p);
  };

  const sinConectar = useMemo<PlataformaDeTienda[]>(() => {
    const ya = new Set((tiendas ?? []).map((t) => t.plataforma));
    return (["tiendanube", "mercadolibre"] as const).filter(
      (p) => !ya.has(p) && (p !== "mercadolibre" || ml.disponible),
    );
  }, [tiendas, ml.disponible]);

  // ── Conectar ──────────────────────────────────────────────────────────────
  const [conectando, setConectando] = useState<PlataformaDeTienda | null>(null);
  const [errorConexion, setErrorConexion] = useState<string | null>(null);

  /** La ventana se abre SINCRÓNICAMENTE en el click, antes de cualquier await (ADR-2026-08-07-tiendanube-popup-safari). */
  const conectar = (p: PlataformaDeTienda) => {
    if (conectando) return;
    const popup =
      p === "tiendanube" ? abrirVentanaOAuth() : abrirVentanaDeMercadoLibre();
    setConectando(p);
    setErrorConexion(null);
    void (async () => {
      try {
        if (p === "tiendanube") {
          await connectTnEnVentana(currentBrandId, popup);
          // Conectar trae el catálogo a Atlas (ADR-2026-10-01-conectar-desde-atlas-trae-el-catalogo).
          void traerCatalogoAlConectar(currentBrandId);
        } else {
          await conectarMercadoLibreEnVentana(currentBrandId, popup);
        }
        elegirTienda(p);
        setVueltaTiendas((n) => n + 1);
      } catch (e) {
        const cancelada =
          (e instanceof Error && e.message === "Conexión cancelada.") ||
          (e instanceof ErrorDeMercadoLibre && e.code === "ventana_cerrada");
        setErrorConexion(
          cancelada
            ? null
            : e instanceof Error
              ? e.message
              : "No se pudo conectar.",
        );
      } finally {
        setConectando(null);
      }
    })();
  };

  // ── Los productos ─────────────────────────────────────────────────────────
  const [consulta, setConsulta] = useState("");
  const [buscada, setBuscada] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setBuscada(consulta.trim()), 350);
    return () => window.clearTimeout(t);
  }, [consulta]);
  // Tiendanube busca en la tienda (nombre, SKU, etiquetas); Mercado Libre, en lo que ya se cargó.
  const buscaEnLaTienda = actual === "tiendanube";
  // ── Filtros ──
  /**
   * Las categorías de la tienda que se arman en Atlas (las mismas del panel de Categorías de la ficha):
   * acá se pueden sumar, renombrar y mover, y cada una sabe cuál es su categoría en Tiendanube.
   */
  const cat = useCategorias(companyId, currentBrandId);
  const [verCategorias, setVerCategorias] = useState(false);
  /** Una categoría del árbol (id de Atlas), `SIN_CATEGORIA` o null = todas. */
  const [categoria, setCategoria] = useState<string | null>(null);
  /**
   * Una categoría real la filtra Tiendanube, por su categoría de allá. Una que todavía no existe en la
   * tienda (creada en Atlas, sin sincronizar) no tiene productos allá: no se le pregunta a Tiendanube y se
   * muestran sólo los nuevos que la tienen. "Sin categoría", la pantalla sobre lo cargado.
   */
  const categoriaReal =
    actual === "tiendanube" && categoria && categoria !== SIN_CATEGORIA
      ? categoria
      : null;
  const tnDeLaCategoria = categoriaReal
    ? (cat.categorias.find((c) => c.id === categoriaReal)?.tnCategoryId ?? null)
    : null;
  const categoriaEnLaTienda =
    tnDeLaCategoria != null ? String(tnDeLaCategoria) : null;
  const categoriaSinTienda =
    !!categoriaReal && tnDeLaCategoria == null && !cat.cargando;
  const [sincronizacion, setSincronizacion] = useState<"todos" | "sin">(
    "todos",
  );
  /** El panel "Filtrar" (sólo Tiendanube, que filtra en su lado). */
  const [filtros, setFiltros] = useState<FiltrosDeLaTienda>({});
  const [verFiltros, setVerFiltros] = useState(false);
  const filtrosEnLaTienda = actual === "tiendanube" ? filtros : {};
  const claveDeFiltros = JSON.stringify(filtrosEnLaTienda);
  useEffect(() => {
    setCategoria(null);
    setSincronizacion("todos");
    setFiltros({});
  }, [actual, currentBrandId]);
  const [lectura, setLectura] = useState<Lectura>({ estado: "cargando" });
  /** La última página que se trajo. Vuelve a 1 cada vez que se relee la primera. */
  const paginaRef = useRef(1);
  const [vueltaProductos, setVueltaProductos] = useState(0);
  const pedidoRef = useRef(0);

  useEffect(() => {
    if (!actual) return;
    const pedido = ++pedidoRef.current;
    if (categoriaSinTienda) {
      setLectura({
        estado: "listo",
        productos: [],
        hayMas: false,
        total: 0,
        cargandoMas: false,
      });
      return;
    }
    setLectura({ estado: "cargando" });
    fetchProductosDeLaTienda({
      brandId: currentBrandId,
      plataforma: actual,
      pagina: 1,
      q: buscaEnLaTienda ? buscada : undefined,
      categoria: categoriaEnLaTienda,
      filtros: filtrosEnLaTienda,
    })
      .then((r) => {
        if (pedido !== pedidoRef.current) return;
        // Cada vez que se relee la primera página (también al volver de sincronizar) se cuenta de nuevo: si no,
        // "Ver más" pedía la página 4 y salteaba la 2 y la 3 (review Greptile de #232).
        paginaRef.current = 1;
        setLectura({ estado: "listo", ...r, cargandoMas: false });
      })
      .catch((e: unknown) => {
        if (pedido !== pedidoRef.current) return;
        const err =
          e instanceof ErrorDeLaTienda ? e : new ErrorDeLaTienda("error");
        setLectura({
          estado: "error",
          mensaje: err.message,
          codigo: err.codigo,
        });
      });
    // La búsqueda de Mercado Libre no relee: filtra lo cargado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actual,
    currentBrandId,
    buscaEnLaTienda ? buscada : "",
    categoriaEnLaTienda,
    categoriaSinTienda,
    claveDeFiltros,
    vueltaProductos,
  ]);


  const cargarMas = async () => {
    if (lectura.estado !== "listo" || lectura.cargandoMas || !actual) return;
    const pedido = pedidoRef.current;
    setLectura({ ...lectura, cargandoMas: true });
    try {
      const r = await fetchProductosDeLaTienda({
        brandId: currentBrandId,
        plataforma: actual,
        pagina: paginaRef.current + 1,
        q: buscaEnLaTienda ? buscada : undefined,
        categoria: categoriaEnLaTienda,
        filtros: filtrosEnLaTienda,
      });
      if (pedido !== pedidoRef.current) return;
      paginaRef.current += 1;
      setLectura((prev) => {
        if (prev.estado !== "listo") return prev;
        const vistos = new Set(prev.productos.map((p) => p.idExterno));
        return {
          ...prev,
          productos: [
            ...prev.productos,
            ...r.productos.filter((p) => !vistos.has(p.idExterno)),
          ],
          hayMas: r.hayMas,
          cargandoMas: false,
        };
      });
    } catch {
      if (pedido === pedidoRef.current)
        setLectura((prev) =>
          prev.estado === "listo" ? { ...prev, cargandoMas: false } : prev,
        );
    }
  };

  // ── Sin sincronizar ───────────────────────────────────────────────────────
  // Los productos de Tiendanube con cambios guardados que la tienda todavía no tiene. Se relee con la
  // lista: al volver de una ficha o después de publicar.
  const [sinSincronizar, setSinSincronizar] = useState<Set<string>>(new Set());
  /** Se relee cuando el asistente cambia la lista de cambios de la tienda. */
  const [vueltaNuevos, setVueltaNuevos] = useState(0);
  /** Los productos agregados desde Atlas que todavía no están en la tienda: van primero en la lista. */
  const [nuevos, setNuevos] = useState<ProductoDeLaTienda[]>([]);
  /** Cuándo se editó en Atlas cada producto de la tienda con cambios sin sincronizar. */
  const [editadosEn, setEditadosEn] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!companyId || actual !== "tiendanube" || ensayo) {
      setSinSincronizar(new Set());
      setNuevos([]);
      setEditadosEn(new Map());
      return;
    }
    let vivo = true;
    void sinSincronizarEnTiendanube(companyId, currentBrandId).then((s) => {
      if (!vivo) return;
      // Los nuevos también están sin sincronizar: cuentan en el filtro y en el botón.
      setSinSincronizar(
        new Set([...s.ids, ...s.nuevos.map((p) => p.idExterno)]),
      );
      setNuevos(s.nuevos);
      setEditadosEn(s.editadosEn);
    });
    return () => {
      vivo = false;
    };
  }, [
    companyId,
    currentBrandId,
    actual,
    ensayo,
    vueltaProductos,
    vueltaNuevos,
  ]);

  const visibles = useMemo(() => {
    if (lectura.estado !== "listo") return [];
    const q = buscada.toLowerCase();
    // Los nuevos (todavía no están en la tienda) se suman a los de la tienda. Sin categoría en la tienda:
    // con una categoría elegida no aparecen; con "Sin categoría", sí.
    const propios =
      actual === "tiendanube" && cuantosFiltros(filtros) === 0
        ? nuevos.filter(
            (p) =>
              (!q || p.nombre.toLowerCase().includes(q)) &&
              (!categoria ||
                (categoria === SIN_CATEGORIA
                  ? (p.categoriasDeAtlas ?? []).length === 0
                  : (p.categoriasDeAtlas ?? []).includes(categoria))),
          )
        : [];
    // Lo último modificado o creado primero: la fecha en la tienda o, si se editó en Atlas y todavía no
    // se sincronizó, la de esa edición (la más nueva de las dos). Sin fecha, al final, en su orden.
    const cuando = (p: ProductoDeLaTienda) => {
      const fechas = [p.actualizadoEn, editadosEn.get(p.idExterno)].filter(
        (f): f is string => !!f,
      );
      return fechas.length
        ? Math.max(...fechas.map((f) => Date.parse(f)))
        : -Infinity;
    };
    return [...propios, ...lectura.productos]
      .map((p, i) => ({ p, i, t: cuando(p) }))
      .sort((a, b) => b.t - a.t || a.i - b.i)
      .map((x) => x.p)
      .filter(
        (p) =>
          (p.ruta ||
            buscaEnLaTienda ||
            !q ||
            p.nombre.toLowerCase().includes(q) ||
            p.idExterno.toLowerCase().includes(q)) &&
          (categoria !== SIN_CATEGORIA || p.categorias.length === 0) &&
          (sincronizacion === "todos" || sinSincronizar.has(p.idExterno)),
      );
  }, [
    lectura,
    buscada,
    buscaEnLaTienda,
    categoria,
    sincronizacion,
    sinSincronizar,
    nuevos,
    editadosEn,
    actual,
    filtros,
  ]);
  const hayFiltros =
    categoria !== null ||
    sincronizacion !== "todos" ||
    cuantosFiltros(filtros) > 0;

  /**
   * El asistente a la derecha, sin salir de la lista (como la mascota), para agregar o editar productos e
   * importar una planilla. Sus herramientas de lanzamiento toman el `lanzamiento_id` que se le pase; sin
   * él agarraban el borrador más reciente, así que va en el contexto el de la lista de cambios de la tienda.
   */
  const { abrirChat, enviarMensaje } = useChatAtlas();
  const [lanzamientoDelAgente, setLanzamientoDelAgente] = useState<
    string | null
  >(null);
  const conElAgente = async (planilla?: File) => {
    if (!companyId || yendo) return;
    setYendo(planilla ? "planilla" : "agente");
    setErrorAbrir(null);
    try {
      const adjunto = planilla
        ? await prepararAdjunto(planilla, companyId)
        : null;
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const id = await lanzamientoDeLaTienda({
        companyId,
        brandId: currentBrandId,
        plataforma: "tiendanube",
        userId: user?.id,
      });
      setLanzamientoDelAgente(id);
      abrirChat({
        contexto: {
          pantalla: "Tienda online",
          ruta: "/lanzamientos",
          detalle:
            "La lista de productos de su Tiendanube. Para agregar productos nuevos o editar los que ya están, " +
            `usá el lanzamiento de la tienda: pasá lanzamiento_id "${id}" en las herramientas de lanzamiento ` +
            "(no crees otro ni uses otro borrador). Lo que agregues se crea en la tienda cuando la persona sincroniza.",
        },
        vista: "panel",
      });
      if (adjunto) {
        window.setTimeout(
          () =>
            enviarMensaje(
              "Agregá a mi Tiendanube los productos de esta planilla.",
              [adjunto],
            ),
          150,
        );
      }
    } catch (e) {
      setErrorAbrir(
        e instanceof Error ? e.message : "No pudimos abrir el asistente.",
      );
    } finally {
      setYendo(null);
    }
  };
  // Lo que el asistente suma o cambia aparece en la lista sin recargar.
  useEffect(() => {
    if (!lanzamientoDelAgente) return;
    return escucharCambiosDeLanzamiento(lanzamientoDelAgente, () =>
      setVueltaNuevos((n) => n + 1),
    );
  }, [lanzamientoDelAgente]);

  /** "Sincronizar" va a la lista de cambios de la tienda; "Agregar producto", a una ficha nueva en blanco. */
  const [yendo, setYendo] = useState<
    "publicar" | "agregar" | "planilla" | "agente" | null
  >(null);
  const irAlLanzamiento = async (para: "publicar" | "agregar") => {
    if (!companyId || yendo) return;
    setYendo(para);
    setErrorAbrir(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (para === "agregar") {
        navigate(
          await crearProductoEnBlanco({
            companyId,
            brandId: currentBrandId,
            userId: user?.id,
          }),
        );
        return;
      }
      const id = await lanzamientoDeLaTienda({
        companyId,
        brandId: currentBrandId,
        plataforma: "tiendanube",
        userId: user?.id,
      });
      // Se sincroniza acá: la lista de cambios de la tienda no se muestra
      // (ADR-2026-10-30-la-lista-de-cambios-no-se-muestra).
      setAvisoSincronizar(null);
      const r = await sincronizarLaTienda({
        lanzamientoId: id,
        companyId,
        brandId: currentBrandId,
        ml: { disponible: ml.disponible, conectada: ml.conectada },
        alAvanzar: (subidas, total) =>
          setProgresoSincronizar({ subidas, total }),
      });
      setAvisoSincronizar(r);
      setVueltaProductos((n) => n + 1);
    } catch (e) {
      setErrorAbrir(
        e instanceof Error ? e.message : "No pudimos sincronizar tu tienda.",
      );
    } finally {
      setYendo(null);
      setProgresoSincronizar(null);
    }
  };
  const [avisoSincronizar, setAvisoSincronizar] =
    useState<ResultadoDeSincronizar | null>(null);

  // ── Edición masiva (ADR-2026-10-30-edicion-masiva) ──
  const [seleccionando, setSeleccionando] = useState(false);
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [verEdicion, setVerEdicion] = useState(false);
  const [aplicando, setAplicando] = useState<string | null>(null);
  const alternarSeleccion = (id: string) =>
    setSeleccion((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const salirDeLaSeleccion = () => {
    setSeleccionando(false);
    setSeleccion(new Set());
  };
  const aplicarEdicion = async (edicion: EdicionMasiva) => {
    if (!companyId || lectura.estado !== "listo") return;
    const productos = visibles.filter((x) => seleccion.has(x.idExterno));
    if (productos.length === 0) return;
    setVerEdicion(false);
    setAplicando("Preparando…");
    setAvisoSincronizar(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const r = await aplicarEdicionMasiva({
        productos,
        edicion,
        companyId,
        brandId: currentBrandId,
        userId: user?.id,
        ml: { disponible: ml.disponible, conectada: ml.conectada },
        alAvanzar: setAplicando,
      });
      setAvisoSincronizar(r);
      salirDeLaSeleccion();
      setVueltaProductos((n) => n + 1);
    } catch (e) {
      setErrorAbrir(
        e instanceof Error ? e.message : "No se pudo aplicar la edición.",
      );
    } finally {
      setAplicando(null);
    }
  };
  const [progresoSincronizar, setProgresoSincronizar] = useState<{
    subidas: number;
    total: number;
  } | null>(null);

  // ── Abrir un producto ─────────────────────────────────────────────────────
  const [abriendo, setAbriendo] = useState<{
    id: string;
    paso: "trayendo" | "abriendo";
  } | null>(null);
  const [errorAbrir, setErrorAbrir] = useState<string | null>(null);
  const [deMl, setDeMl] = useState<{
    producto: ProductoDeLaTienda;
    varianteId: string | null;
  } | null>(null);

  const abrir = useCallback(
    async (p: ProductoDeLaTienda) => {
      if (!companyId || abriendo) return;
      setErrorAbrir(null);
      // Uno nuevo (todavía no está en la tienda): su ficha en la lista de cambios.
      if (p.ruta) {
        navigate(p.ruta);
        return;
      }
      if (actual === "mercadolibre") {
        // El panel se abre enseguida con lo que ya se sabe; el bloque de Mercado Libre, cuando está en Atlas.
        setDeMl({ producto: p, varianteId: null });
        try {
          setAbriendo({
            id: p.idExterno,
            paso: p.productoId ? "abriendo" : "trayendo",
          });
          const { varianteId } = await varianteDeMercadoLibre(p, {
            companyId,
            brandId: currentBrandId,
          });
          setDeMl((prev) =>
            prev?.producto.idExterno === p.idExterno
              ? { producto: p, varianteId }
              : prev,
          );
        } catch (e) {
          setErrorAbrir(
            e instanceof Error ? e.message : "No pudimos abrir el producto.",
          );
        } finally {
          setAbriendo(null);
        }
        return;
      }
      setAbriendo({
        id: p.idExterno,
        paso: p.productoId ? "abriendo" : "trayendo",
      });
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const ruta = await abrirProductoDeTiendanube(
          p,
          { userId: user?.id, companyId, brandId: currentBrandId },
          (paso) => setAbriendo({ id: p.idExterno, paso }),
        );
        navigate(ruta);
      } catch (e) {
        setErrorAbrir(
          e instanceof Error ? e.message : "No pudimos abrir el producto.",
        );
        setAbriendo(null);
      }
    },
    [companyId, abriendo, actual, currentBrandId, navigate],
  );

  const tienda = tiendas?.find((t) => t.plataforma === actual) ?? null;
  const verMas =
    lectura.estado === "listo" && lectura.hayMas ? (
<div className="mt-6 flex justify-center">
                      <button
                        type="button"
                        onClick={() => void cargarMas()}
                        disabled={lectura.cargandoMas}
                        className={boton("secundario")}
                      >
                        {lectura.cargandoMas ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : null}
                        {lectura.cargandoMas
                          ? "Cargando…"
                          : "Ver más productos"}
                      </button>
                    </div>
    ) : null;
  // También mientras llega la empresa: sin esto se veía el buscador sin pestañas un instante.
  const cargandoTiendas = tiendas === null && !errorTiendas;

  return (
    <div className="relative isolate mx-auto max-w-6xl pt-1 pb-6 px-4 md:py-10 md:px-8">
      <HaloDeMarca paleta={paleta} escritorio={escritorio} />
      <div className="flex items-center justify-between gap-4">
        <h1 className="pl-1 text-[26px] font-semibold leading-tight tracking-[-0.01em] text-brand-black sm:pl-0 sm:text-xl sm:tracking-normal md:text-2xl">
          Tienda online
        </h1>
      </div>
      <p className="mt-1 hidden max-w-2xl text-sm text-neutral-500 sm:block">
        Los productos de tus tiendas.
      </p>

      {errorTiendas ? (
        <ErrorDeLectura
          mensaje={errorTiendas}
          onReintentar={() => setVueltaTiendas((n) => n + 1)}
        />
      ) : cargandoTiendas ? (
        <Esqueleto />
      ) : tiendas && tiendas.length === 0 ? (
        <SinTiendas
          opciones={sinConectar}
          conectando={conectando}
          error={errorConexion}
          onConectar={conectar}
        />
      ) : (
        <>
          {/* Una pestaña por tienda. Con una sola igual se muestra: dice de dónde son los productos. */}
          {/* `overflow-x-auto` (scroll en el teléfono) recorta lo que sale de la caja, sombra incluida:
              el aire de adentro es el de la sombra de la elegida, y los márgenes negativos lo devuelven. */}
          <div
            role="tablist"
            aria-label="Tus tiendas"
            className="-mx-4 -mb-4 mt-1 flex gap-2 px-4 pt-4 pb-6 sm:mt-2 sm:gap-3 sm:overflow-x-auto"
          >
            {(tiendas ?? []).map((t) => (
              <SelectorDeTienda
                key={t.plataforma}
                tienda={t}
                activa={t.plataforma === actual}
                onElegir={() => elegirTienda(t.plataforma)}
              />
            ))}
            {sinConectar.map((p) => (
              <PillDeConectar
                key={p}
                plataforma={p}
                conectando={conectando === p}
                bloqueada={!!conectando}
                onConectar={() => conectar(p)}
              />
            ))}
            {/* En el teléfono no: eran dos pantallas de ancho de cosas que todavía no se pueden usar. */}
            {PROXIMAMENTE.map((p) => (
              <div key={p.id} className="hidden sm:block">
                <PillProximamente {...p} />
              </div>
            ))}
          </div>
          {errorConexion && (
            <p role="alert" className="mt-1 text-xs text-red-600">
              {errorConexion}
            </p>
          )}

          {tienda?.reconectar ? (
            <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
              <p className="font-medium">
                Tu cuenta de {NOMBRE[tienda.plataforma]} se desconectó.
              </p>
              <p className="mt-1">Volvé a conectarla para ver tus productos.</p>
              <button
                type="button"
                onClick={() => conectar(tienda.plataforma)}
                disabled={!!conectando}
                className={boton("primario", { extra: "mt-4" })}
              >
                {conectando ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : null}
                Volver a conectar
              </button>
            </div>
          ) : (
            <>
              {/* Primera línea: las acciones. Abajo, el buscador con los filtros. */}
              <div className="mt-3 flex items-center gap-2 sm:mt-4 sm:flex-wrap sm:justify-end">
                {/* Por ahora sólo Tiendanube: en Mercado Libre la ficha todavía no crea ni publica desde acá. */}
                {actual === "tiendanube" && !tienda?.reconectar && (
                  <div className="flex w-full min-w-0 items-center gap-1.5 sm:w-auto sm:shrink-0 sm:gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        actual &&
                        navigate("/lanzamientos/exportar-importar", {
                          state: {
                            plataforma: actual,
                            q: buscaEnLaTienda ? buscada : undefined,
                            categoria: categoriaEnLaTienda,
                            filtros: filtrosEnLaTienda,
                            buscarAca: buscaEnLaTienda ? undefined : buscada || undefined,
                            sinCategoria: categoria === SIN_CATEGORIA,
                            soloSinSincronizar:
                              sincronizacion === "sin" ? [...sinSincronizar] : undefined,
                            categoriaSinTienda,
                          } satisfies DesdeLaLista,
                        })
                      }
                      aria-label="Exportar e importar"
                      className={boton("secundario", {
                        extra: "w-11 shrink-0 !px-0 sm:w-auto sm:!px-5 [&>svg]:shrink-0",
                      })}
                    >
                      <ArrowDownToLine className="h-4 w-4" />
                      <span className="hidden sm:inline">Exportar e importar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void conElAgente()}
                      disabled={!!yendo}
                      title="Abre el asistente para agregar o editar productos con IA"
                      className={boton("secundario", {
                        extra:
                          "w-11 shrink-0 !px-0 sm:w-auto sm:!px-5 [&>svg]:shrink-0",
                      })}
                    >
                      {yendo === "agente" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      <span className="hidden sm:inline">Agregar con IA</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        seleccionando
                          ? salirDeLaSeleccion()
                          : setSeleccionando(true)
                      }
                      disabled={!!yendo || !!aplicando}
                      aria-pressed={seleccionando}
                      title="Elegí varios productos y cambialos a la vez, en tus tiendas"
                      className={boton(
                        seleccionando ? "contorno" : "secundario",
                        {
                          extra:
                            "w-11 shrink-0 !px-0 sm:w-auto sm:!px-5 [&>svg]:shrink-0",
                        },
                      )}
                    >
                      <ListChecks className="h-4 w-4" />
                      <span className="hidden sm:inline">
                        {seleccionando ? "Cancelar lote" : "Editar en lote"}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void irAlLanzamiento("agregar")}
                      disabled={!!yendo}
                      aria-label="Agregar producto"
                      className={boton("secundario", {
                        extra:
                          "w-11 shrink-0 !px-0 sm:w-auto sm:!px-5 [&>svg]:shrink-0",
                      })}
                    >
                      {yendo === "agregar" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      <span className="hidden sm:inline">Agregar producto</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => void irAlLanzamiento("publicar")}
                      disabled={!!yendo || sinSincronizar.size === 0}
                      title={
                        sinSincronizar.size === 0
                          ? "No hay cambios para sincronizar"
                          : `${sinSincronizar.size === 1 ? "Un producto tiene" : `${sinSincronizar.size} productos tienen`} cambios sin sincronizar`
                      }
                      className={boton("primario", {
                        extra: "min-w-0 flex-1 px-3 sm:flex-none sm:px-5",
                      })}
                    >
                      {yendo === "publicar" ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      {yendo === "publicar"
                        ? progresoSincronizar
                          ? `Sincronizando ${progresoSincronizar.subidas}/${progresoSincronizar.total}`
                          : "Sincronizando…"
                        : "Sincronizar"}
                      {yendo !== "publicar" && sinSincronizar.size > 0 && (
                        <span className="rounded-md bg-white/20 py-0.5 px-1.5 text-xs tabular-nums">
                          {sinSincronizar.size}
                        </span>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <div className="relative min-w-0 flex-1 sm:min-w-[220px]">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="search"
                    value={consulta}
                    onChange={(e) => setConsulta(e.target.value)}
                    placeholder={
                      actual === "tiendanube"
                        ? "Buscar por nombre o SKU"
                        : "Buscar por título"
                    }
                    aria-label="Buscar productos"
                    className="w-full rounded-xl border border-neutral-200 bg-white py-2.5 pl-10 pr-4 text-[16px] text-brand-black outline-none transition-shadow placeholder:text-neutral-400 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.1)] sm:text-sm"
                  />
                </div>
                {actual === "tiendanube" && (
                  <>
                    <button
                      type="button"
                      onClick={() => setVerFiltros(true)}
                      aria-label="Filtrar productos"
                      className={`inline-flex h-[var(--btn-h)] shrink-0 items-center gap-2 rounded-full border px-3 sm:px-4 text-[13.5px] font-medium shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors ${
                        cuantosFiltros(filtros) > 0
                          ? "border-transparent bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary"
                          : "border-black/[0.08] bg-white text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900"
                      }`}
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      <span className="hidden sm:inline">Filtrar</span>
                      {cuantosFiltros(filtros) > 0 && (
                        <span className="rounded-full bg-brand-primary py-px px-1.5 text-[10.5px] font-semibold tabular-nums text-white">
                          {cuantosFiltros(filtros)}
                        </span>
                      )}
                    </button>
                    <FiltroDeCategoria
                      categorias={cat.categorias}
                      cargando={cat.cargando}
                      filtro={categoria}
                      onCambiar={setCategoria}
                      onEditar={() => setVerCategorias(true)}
                    />
                    <div className="basis-full sm:basis-auto">
                      <FiltroDeSincronizacion
                        valor={sincronizacion}
                        pendientes={sinSincronizar.size}
                        onCambiar={setSincronizacion}
                      />
                    </div>
                  </>
                )}
              </div>

              {avisoSincronizar && (
                <div
                  role={avisoSincronizar.error ? "alert" : "status"}
                  className={`mt-4 flex items-start gap-2 rounded-xl border py-3 px-4 text-sm ${
                    avisoSincronizar.error
                      ? "border-red-200 bg-red-50 text-red-700"
                      : "border-emerald-200 bg-emerald-50 text-emerald-800"
                  }`}
                >
                  {avisoSincronizar.error ? (
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  ) : (
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  )}
                  <span className="flex-1">
                    {avisoSincronizar.texto}
                    {avisoSincronizar.abrir && (
                      <button
                        type="button"
                        onClick={() => navigate(avisoSincronizar.abrir!)}
                        className="ml-2 font-medium underline"
                      >
                        Completarlo
                      </button>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setAvisoSincronizar(null)}
                    aria-label="Cerrar"
                    className="opacity-60 hover:opacity-100"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {errorAbrir && !deMl && (
                <div
                  role="alert"
                  className="mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 py-3 px-4 text-sm text-red-700"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                  <span className="flex-1">{errorAbrir}</span>
                  <button
                    type="button"
                    onClick={() => setErrorAbrir(null)}
                    aria-label="Cerrar"
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {lectura.estado === "cargando" ? (
                <Esqueleto sinPestanas />
              ) : lectura.estado === "error" ? (
                <ErrorDeLectura
                  mensaje={lectura.mensaje}
                  onReintentar={() => setVueltaProductos((n) => n + 1)}
                />
              ) : visibles.length === 0 ? (
                <>
                <p className="mt-10 text-center text-sm text-neutral-500">
                  {buscada
                    ? `No hay productos que coincidan con “${buscada}”.`
                    : categoriaSinTienda
                      ? "Esta categoría todavía no está en tu tienda: aparece cuando sincronices un producto que la tenga."
                      : hayFiltros
                        ? "No hay productos con estos filtros."
                        : `Tu ${NOMBRE[actual!]} todavía no tiene productos.`}
                </p>
                {/* Los filtros de acá (Sin sincronizar, Sin categoría, la búsqueda en Mercado Libre) miran sólo lo
                    cargado: si en esta página no hay, puede haber en la que sigue. */}
                {verMas}
                </>
              ) : (
                <>
                  {/* Teléfono: filas. */}
                  <ul
                    key={`${sincronizacion}:${categoria ?? ""}`}
                    className="mt-6 animate-[aparecer-suave_0.35s_ease-out_both] divide-y motion-reduce:animate-none divide-neutral-100 overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card shadow-sm sm:hidden"
                  >
                    {visibles.map((p) => (
                      <li key={p.idExterno}>
                        <FilaDeProducto
                          producto={p}
                          sinSincronizar={sinSincronizar.has(p.idExterno)}
                          seleccion={
                            seleccionando ? seleccion.has(p.idExterno) : null
                          }
                          abriendo={
                            abriendo?.id === p.idExterno ? abriendo.paso : null
                          }
                          onAbrir={() =>
                            seleccionando
                              ? alternarSeleccion(p.idExterno)
                              : void abrir(p)
                          }
                        />
                      </li>
                    ))}
                  </ul>
                  {/* Compu: grilla de cards, como el panel de una tienda. */}
                  {/* La `key` con el filtro vuelve a correr la entrada: el cambio se ve como un fundido. */}
                  <div
                    key={`${sincronizacion}:${categoria ?? ""}`}
                    className="mt-6 hidden animate-[aparecer-suave_0.35s_ease-out_both] gap-4 motion-reduce:animate-none sm:grid sm:grid-cols-3 lg:grid-cols-4"
                  >
                    {visibles.map((p) => (
                      <CardDeProducto
                        key={p.idExterno}
                        producto={p}
                        sinSincronizar={sinSincronizar.has(p.idExterno)}
                        seleccion={
                          seleccionando ? seleccion.has(p.idExterno) : null
                        }
                        abriendo={
                          abriendo?.id === p.idExterno ? abriendo.paso : null
                        }
                        onAbrir={() =>
                          seleccionando
                            ? alternarSeleccion(p.idExterno)
                            : void abrir(p)
                        }
                      />
                    ))}
                  </div>
                  {verMas}
                </>
              )}
            </>
          )}
        </>
      )}

      {metricasVisibles && (
        <div className="mt-8 sm:mt-12 sm:border-t sm:border-neutral-200 sm:pt-4">
          <ResumenDeMetricas forzarSinTiendas={ensayo} />
        </div>
      )}

      {(seleccionando || aplicando) && (
        <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4 md:bottom-6">
          <div className="flex w-full max-w-xl items-center gap-2 rounded-2xl border border-neutral-200 bg-white/95 py-2 pl-4 pr-2 shadow-xl backdrop-blur">
            {aplicando ? (
              <span className="flex flex-1 items-center gap-2 py-2 text-sm font-medium text-brand-black">
                <Loader2 className="h-4 w-4 animate-spin" /> {aplicando}
              </span>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium text-brand-black">
                  {seleccion.size === 0
                    ? "Tocá los productos que querés cambiar"
                    : `${seleccion.size} seleccionado${seleccion.size === 1 ? "" : "s"}`}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setSeleccion(new Set(visibles.map((x) => x.idExterno)))
                  }
                  className="rounded-lg px-2.5 py-2 text-sm font-medium text-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.07)]"
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={salirDeLaSeleccion}
                  className={boton("neutro", { chico: true })}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={seleccion.size === 0}
                  onClick={() => setVerEdicion(true)}
                  className={boton("primario", { chico: true })}
                >
                  Editar
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <PanelDeEdicionMasiva
        abierto={verEdicion}
        cuantos={seleccion.size}
        // Con una sola tienda conectada, no hay dónde elegir.
        conMl={ml.disponible && ml.conectada}
        onCerrar={() => setVerEdicion(false)}
        onAplicar={(e) => void aplicarEdicion(e)}
      />

      <PanelDeFiltros
        abierto={verFiltros}
        filtros={filtros}
        categoria={categoria}
        categorias={cat.categorias}
        onCerrar={() => setVerFiltros(false)}
        onAplicar={(f, c) => {
          setFiltros(f);
          setCategoria(c);
          setVerFiltros(false);
        }}
      />

      <PanelDeCategorias
        abierto={verCategorias}
        categorias={cat.categorias}
        cargando={cat.cargando}
        onCerrar={() => setVerCategorias(false)}
        onCrear={cat.crear}
        onRenombrar={cat.renombrar}
        onMover={cat.mover}
        onBorrar={cat.borrar}
      />

      {deMl && (
        <Modal
          titulo={deMl.producto.nombre}
          onCerrar={() => {
            setDeMl(null);
            setErrorAbrir(null);
          }}
        >
          <DetalleDeMercadoLibre
            producto={deMl.producto}
            varianteId={deMl.varianteId}
            companyId={companyId}
            trayendo={abriendo?.id === deMl.producto.idExterno}
            error={errorAbrir}
          />
        </Modal>
      )}
    </div>
  );
}

/** El color de cada plataforma: el cuadrado del logo, el borde de la elegida y su halo. */
const MARCA: Record<
  PlataformaDeTienda,
  { cuadrado: string; anillo: string; fondo: string }
> = {
  tiendanube: {
    cuadrado: "#1E5BE6",
    anillo: "#1E5BE6",
    fondo: "rgba(30,91,230,0.08)",
  },
  mercadolibre: {
    cuadrado: "#FFE600",
    anillo: "#E6C700",
    fondo: "rgba(255,230,0,0.18)",
  },
};

/** El logo de la plataforma en su cuadrado de color, como el ícono de la app de cada una. */
function LogoDePlataforma({ plataforma }: { plataforma: PlataformaDeTienda }) {
  return plataforma === "tiendanube" ? (
    <TiendaNubeIcon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
  ) : (
    // El PNG es el óvalo sobre fondo blanco: se recorta con una elipse al borde del óvalo, así el blanco de
    // las esquinas no se ve y el óvalo conserva sus colores (con `multiply` se teñía de amarillo).
    <img
      src="/images/mercado-libre.png"
      alt=""
      className="h-7 w-7 object-contain sm:h-10 sm:w-10"
      style={{ clipPath: "ellipse(45.5% 31.5% at 50% 50%)" }}
    />
  );
}

/**
 * Una tienda conectada: el logo de la marca grande y a color, el nombre y la cuenta. La elegida se
 * levanta (sombra y el borde en el color de la plataforma); las otras quedan igual de nítidas, con
 * borde gris: apagarlas las hacía parecer deshabilitadas.
 */
function SelectorDeTienda({
  tienda: t,
  activa,
  onElegir,
}: {
  tienda: TiendaConectada;
  activa: boolean;
  onElegir: () => void;
}) {
  const m = MARCA[t.plataforma];
  return (
    <button
      type="button"
      role="tab"
      aria-selected={activa}
      onClick={onElegir}
      style={
        activa
          ? {
              borderColor: m.anillo,
              boxShadow: `0 0 0 3px ${m.fondo}, 0 8px 24px -12px rgba(15,23,42,0.25)`,
            }
          : undefined
      }
      className={`group relative flex min-w-0 flex-1 items-center gap-2.5 rounded-xl border py-1.5 pl-1.5 pr-3 text-left transition-all duration-200 sm:min-w-[220px] sm:flex-none sm:shrink-0 sm:gap-3 sm:rounded-2xl sm:py-3 sm:pl-3 sm:pr-5 ${
        activa
          ? "bg-white"
          : "border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-sm"
      }`}
    >
      <span
        style={{ backgroundColor: m.cuadrado }}
        className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg shadow-sm ring-1 ring-black/5 sm:h-11 sm:w-11 sm:rounded-xl"
      >
        <LogoDePlataforma plataforma={t.plataforma} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-semibold leading-tight text-brand-black sm:text-[15px]">
          {NOMBRE[t.plataforma]}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-neutral-500 sm:text-xs">
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${t.reconectar ? "bg-amber-400" : "bg-emerald-500"}`}
          />
          <span className="min-w-0 truncate sm:max-w-[160px]">
            {t.reconectar ? "Hay que reconectar" : t.nombre || "Conectada"}
          </span>
        </span>
      </span>
    </button>
  );
}

/** ≥1024px, como el `escritorio` de Mi marca: decide la máscara del halo. */
function useEsEscritorio(): boolean {
  const consulta = "(min-width: 1024px)";
  const [es, setEs] = useState(
    () => typeof window !== "undefined" && window.matchMedia(consulta).matches,
  );
  useEffect(() => {
    const m = window.matchMedia(consulta);
    const cambio = () => setEs(m.matches);
    m.addEventListener("change", cambio);
    return () => m.removeEventListener("change", cambio);
  }, []);
  return es;
}

function Foto({ src, className }: { src: string | null; className: string }) {
  return src ? (
    <img
      src={src}
      alt=""
      loading="lazy"
      className={`${className} object-cover`}
    />
  ) : (
    <span
      className={`${className} grid place-items-center bg-gradient-to-br from-[rgb(var(--brand-primary-rgb)_/_0.095)] to-[#E3FAFC] text-[rgb(var(--brand-primary-rgb)_/_0.4)]`}
    >
      <Images className="h-5 w-5" />
    </span>
  );
}

function TextoAbriendo({ paso }: { paso: "trayendo" | "abriendo" }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-primary">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {paso === "trayendo" ? "Trayendo el producto…" : "Abriendo…"}
    </span>
  );
}

function PrecioYStock({ producto: p }: { producto: ProductoDeLaTienda }) {
  const s = textoDeStock(p.stock);
  const conPromo =
    p.precioPromocional !== null &&
    p.precio !== null &&
    p.precioPromocional < p.precio;
  return (
    <>
      <span className="text-sm font-semibold leading-snug tabular-nums text-brand-black">
        {conPromo
          ? formatoPrecio(p.precioPromocional, p.moneda)
          : formatoPrecio(p.precio, p.moneda)}
        {conPromo && (
          <span className="ml-1.5 text-xs font-normal text-neutral-400 line-through">
            {formatoPrecio(p.precio, p.moneda)}
          </span>
        )}
      </span>
      <span
        className={`text-xs leading-snug ${s.agotado ? "text-red-600" : "text-neutral-500"}`}
      >
        {s.texto}
        {p.variantes > 1 && ` · ${p.variantes} variantes`}
      </span>
    </>
  );
}

function FilaDeProducto({
  producto: p,
  sinSincronizar,
  seleccion,
  abriendo,
  onAbrir,
}: {
  producto: ProductoDeLaTienda;
  sinSincronizar: boolean;
  /** En modo selección: si está elegido. null = no se está eligiendo. */
  seleccion: boolean | null;
  abriendo: "trayendo" | "abriendo" | null;
  onAbrir: () => void;
}) {
  const e = ESTADO[p.estado];
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-pressed={seleccion ?? undefined}
      className={`flex w-full items-center gap-3 py-3 pl-3 pr-2 text-left transition-colors active:bg-neutral-50 ${seleccion ? "bg-[rgb(var(--brand-primary-rgb)_/_0.05)]" : ""}`}
    >
      {seleccion !== null && <CasillaDeSeleccion elegida={seleccion} />}
      <Foto src={p.foto} className="h-14 w-14 shrink-0 rounded-xl" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-[15px] font-medium leading-snug text-brand-black">
          {p.nombre}
        </span>
        {abriendo ? (
          <TextoAbriendo paso={abriendo} />
        ) : (
          <span className="flex flex-wrap items-baseline gap-x-2">
            <PrecioYStock producto={p} />
          </span>
        )}
        {e && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-600">
            <span
              className={`h-1.5 w-1.5 rounded-full ${e.punto}`}
              aria-hidden="true"
            />
            {e.texto}
          </span>
        )}
        {sinSincronizar && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-primary">
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            Sin sincronizar con Tiendanube
          </span>
        )}
        {textoDeLoQueFalta(p.faltan) && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
            <AlertCircle className="h-3 w-3" aria-hidden="true" />
            {textoDeLoQueFalta(p.faltan)}
          </span>
        )}
      </span>
      <ChevronRight
        className="h-4 w-4 shrink-0 text-neutral-300"
        aria-hidden="true"
      />
    </button>
  );
}

/** Las tiendas que todavía no se pueden conectar: se muestran para que se sepa que vienen. */
const PROXIMAMENTE: Array<{
  id: string;
  nombre: string;
  logo: React.ReactNode;
  fondo: string;
}> = [
  {
    id: "shopify",
    nombre: "Shopify",
    logo: (
      <img
        src="/images/logos_plataformas/shopify.png"
        alt=""
        className="h-8 w-8 object-contain"
      />
    ),
    fondo: "#FFFFFF",
  },
  {
    id: "vtex",
    nombre: "VTEX",
    logo: (
      <span className="[&>svg]:h-7 [&>svg]:w-7">{getPlatformLogo("vtex")}</span>
    ),
    fondo: "#FFFFFF",
  },
  {
    id: "empretienda",
    nombre: "Empretienda",
    logo: (
      <span className="[&>svg]:h-7 [&>svg]:w-7">
        {getPlatformLogo("empretienda")}
      </span>
    ),
    fondo: "#FFFFFF",
  },
];

/**
 * En una grilla de dos columnas en el teléfono (el estado vacío): la pill llena su celda y achica logo,
 * márgenes y letra para que "Mercado Libre" entre. Desde `sm` es la de siempre.
 */
const PILL_EN_GRILLA = {
  pill: "w-full min-w-0 gap-2.5 py-2.5 pl-2.5 pr-3 sm:w-auto sm:gap-3 sm:py-3 sm:pl-3 sm:pr-5",
  logo: "h-9 w-9 rounded-lg sm:h-11 sm:w-11 sm:rounded-xl",
  nombre: "text-sm sm:text-[15px]",
};
const PILL_SUELTA = {
  pill: "gap-3 py-3 pl-3 pr-5",
  logo: "h-11 w-11 rounded-xl",
  nombre: "text-[15px]",
};

/** Una tienda para conectar, a la derecha de las conectadas y con su mismo alto. */
function PillDeConectar({
  plataforma,
  conectando,
  bloqueada,
  onConectar,
  enGrilla = false,
}: {
  plataforma: PlataformaDeTienda;
  conectando: boolean;
  bloqueada: boolean;
  onConectar: () => void;
  enGrilla?: boolean;
}) {
  const t = enGrilla ? PILL_EN_GRILLA : PILL_SUELTA;
  return (
    <button
      type="button"
      onClick={onConectar}
      disabled={bloqueada}
      className={`group flex shrink-0 items-center rounded-2xl border border-dashed border-neutral-300 bg-white/70 text-left transition-colors hover:border-brand-primary hover:bg-white disabled:opacity-60 ${t.pill}`}
    >
      <span
        style={{ backgroundColor: MARCA[plataforma].cuadrado }}
        className={`grid shrink-0 place-items-center overflow-hidden shadow-sm ring-1 ring-black/5 ${t.logo}`}
      >
        <LogoDePlataforma plataforma={plataforma} />
      </span>
      <span className="min-w-0">
        <span className={`block font-semibold leading-tight text-brand-black ${t.nombre}`}>
          {NOMBRE[plataforma]}
        </span>
        <span className="mt-0.5 flex items-center gap-1 text-xs font-medium text-brand-primary">
          {conectando ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Plus className="h-3 w-3" />
          )}
          {conectando ? "Conectando…" : "Conectar"}
        </span>
      </span>
    </button>
  );
}

function PillProximamente({
  nombre,
  logo,
  fondo,
  enGrilla = false,
}: {
  nombre: string;
  logo: React.ReactNode;
  fondo: string;
  enGrilla?: boolean;
}) {
  const t = enGrilla ? PILL_EN_GRILLA : PILL_SUELTA;
  return (
    <div
      aria-disabled="true"
      className={`flex shrink-0 items-center rounded-2xl border border-dashed border-neutral-200 bg-white/50 ${t.pill}`}
    >
      <span
        style={{ backgroundColor: fondo }}
        className={`grid shrink-0 place-items-center overflow-hidden ring-1 ring-black/5 ${t.logo}`}
      >
        {logo}
      </span>
      <span className="min-w-0">
        <span className={`block font-semibold leading-tight text-neutral-500 ${t.nombre}`}>
          {nombre}
        </span>
        <span className="mt-0.5 block text-xs text-neutral-400">
          Próximamente
        </span>
      </span>
    </div>
  );
}

/** Todos / Sin sincronizar, con el alto del filtro de categoría. */
function FiltroDeSincronizacion({
  valor,
  pendientes,
  onCambiar,
}: {
  valor: "todos" | "sin";
  pendientes: number;
  onCambiar: (v: "todos" | "sin") => void;
}) {
  const opciones = [
    { id: "todos", texto: "Todos" },
    { id: "sin", texto: "Sin sincronizar" },
  ] as const;
  // La píldora de la elegida se desliza de una opción a la otra: se mide la opción elegida y se mueve
  // un fondo absoluto con transición, en vez de pintar el fondo en el botón (que salta).
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [pildora, setPildora] = useState<{
    left: number;
    width: number;
  } | null>(null);
  useEffect(() => {
    const medir = () => {
      const b = refs.current[valor];
      if (b) setPildora({ left: b.offsetLeft, width: b.offsetWidth });
    };
    medir();
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, [valor, pendientes]);
  return (
    <div
      role="radiogroup"
      aria-label="Filtrar por sincronización"
      className="relative inline-flex h-[var(--btn-h)] shrink-0 items-center gap-0.5 rounded-full border border-black/[0.08] bg-white p-1 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
    >
      {pildora && (
        <span
          aria-hidden="true"
          className="absolute inset-y-1 rounded-full bg-[rgb(var(--brand-primary-rgb)_/_0.085)] transition-[left,width] duration-300 ease-out motion-reduce:transition-none"
          style={{ left: pildora.left, width: pildora.width }}
        />
      )}
      {opciones.map((o) => {
        const activa = valor === o.id;
        return (
          <button
            key={o.id}
            ref={(el) => {
              refs.current[o.id] = el;
            }}
            type="button"
            role="radio"
            aria-checked={activa}
            onClick={() => onCambiar(o.id)}
            className={`relative inline-flex h-full items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium transition-colors duration-300 ${
              activa
                ? "text-brand-primary"
                : "text-neutral-600 hover:text-neutral-900"
            }`}
          >
            {o.texto}
            {o.id === "sin" && pendientes > 0 && (
              <span className="rounded-full bg-brand-primary py-px px-1.5 text-[10.5px] font-semibold tabular-nums text-white">
                {pendientes}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Abajo a la izquierda, sobre la foto: lo que le falta para poder venderse. */
function PillDeLoQueFalta({ producto: p }: { producto: ProductoDeLaTienda }) {
  const texto = textoDeLoQueFalta(p.faltan);
  if (!texto) return null;
  return (
    <span className="absolute bottom-2.5 left-2.5 inline-flex max-w-[calc(100%-20px)] items-center gap-1.5 rounded-full bg-white/95 py-1 pl-2 pr-2.5 text-[11px] font-medium text-amber-800 shadow-sm ring-1 ring-amber-200/70 backdrop-blur">
      <AlertCircle
        className="h-3.5 w-3.5 shrink-0 text-amber-500"
        aria-hidden="true"
      />
      <span className="truncate">{texto}</span>
    </span>
  );
}

function CardDeProducto({
  producto: p,
  sinSincronizar,
  seleccion,
  abriendo,
  onAbrir,
}: {
  producto: ProductoDeLaTienda;
  sinSincronizar: boolean;
  /** En modo selección: si está elegido. null = no se está eligiendo. */
  seleccion: boolean | null;
  abriendo: "trayendo" | "abriendo" | null;
  onAbrir: () => void;
}) {
  const e = ESTADO[p.estado];
  return (
    <button
      type="button"
      onClick={onAbrir}
      aria-pressed={seleccion ?? undefined}
      className={`group flex flex-col overflow-hidden rounded-2xl border bg-surface-card text-left shadow-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-md ${
        seleccion
          ? "border-brand-primary shadow-[0_0_0_3px_rgb(var(--brand-primary-rgb)_/_0.12)]"
          : "border-neutral-200 hover:border-neutral-300"
      }`}
    >
      <span className="relative block aspect-square overflow-hidden bg-neutral-100">
        <Foto
          src={p.foto}
          className="h-full w-full transition-transform duration-500 group-hover:scale-[1.03]"
        />
        {seleccion !== null && (
          <span className="absolute inset-0 z-10 bg-black/0 p-2.5">
            <CasillaDeSeleccion elegida={seleccion} />
          </span>
        )}
        {e && (
          <span className="absolute left-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full bg-white/95 py-1 px-2.5 text-[11px] font-medium text-neutral-700 shadow-sm">
            <span
              className={`h-1.5 w-1.5 rounded-full ${e.punto}`}
              aria-hidden="true"
            />
            {e.texto}
          </span>
        )}
        {sinSincronizar && (
          <span
            title="Tiene cambios guardados que todavía no se publicaron en Tiendanube"
            className="absolute right-2.5 top-2.5 inline-flex items-center gap-1.5 rounded-full bg-brand-primary py-1 pl-2 pr-2.5 text-[11px] font-medium text-white shadow-sm"
          >
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
            Sin sincronizar
          </span>
        )}
        <PillDeLoQueFalta producto={p} />
      </span>
      {/* Nombre, precio y stock con el mismo aire entre los tres; el precio se distingue por el peso. */}
      <span className="flex flex-1 flex-col gap-1.5 p-3.5">
        <span className="line-clamp-2 text-sm leading-snug text-brand-black">
          {p.nombre}
        </span>
        {abriendo ? (
          <TextoAbriendo paso={abriendo} />
        ) : (
          <PrecioYStock producto={p} />
        )}
      </span>
    </button>
  );
}

function Esqueleto({ sinPestanas = false }: { sinPestanas?: boolean }) {
  return (
    <div aria-busy="true" aria-label="Cargando tus productos">
      {!sinPestanas && (
        <div className="mt-5 flex gap-2 sm:mt-6">
          <div className="h-[52px] w-44 animate-pulse rounded-2xl bg-neutral-100" />
        </div>
      )}
      {!sinPestanas && (
        <div className="mt-4 h-11 animate-pulse rounded-xl bg-neutral-100" />
      )}
      <div className="mt-6 divide-y divide-neutral-100 overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card sm:hidden">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 py-3 pl-3 pr-2">
            <div className="h-14 w-14 shrink-0 animate-pulse rounded-xl bg-neutral-100" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-2/3 animate-pulse rounded-full bg-neutral-200" />
              <div className="h-3 w-1/3 animate-pulse rounded-full bg-neutral-100" />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 hidden gap-4 sm:grid sm:grid-cols-3 lg:grid-cols-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <div
            key={i}
            className="overflow-hidden rounded-2xl border border-neutral-200 bg-surface-card"
          >
            <div className="aspect-square animate-pulse bg-neutral-100" />
            <div className="space-y-2 p-3.5">
              <div className="h-3.5 w-3/4 animate-pulse rounded-full bg-neutral-200" />
              <div className="h-3 w-1/3 animate-pulse rounded-full bg-neutral-100" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorDeLectura({
  mensaje,
  onReintentar,
}: {
  mensaje: string;
  onReintentar: () => void;
}) {
  return (
    <div className="mt-8 rounded-2xl border border-neutral-200 bg-white p-6 text-center">
      <AlertCircle className="mx-auto h-7 w-7 text-neutral-300" />
      <p className="mt-3 text-sm text-brand-black">{mensaje}</p>
      <button
        type="button"
        onClick={onReintentar}
        className={boton("secundario", { extra: "mt-4" })}
      >
        <RotateCcw className="h-4 w-4" />
        Reintentar
      </button>
    </div>
  );
}

function SinTiendas(props: {
  opciones: PlataformaDeTienda[];
  conectando: PlataformaDeTienda | null;
  error: string | null;
  onConectar: (p: PlataformaDeTienda) => void;
}) {
  const hayVideo = useHayVideoDePresentacion();
  return (
    <>
      <div className="mt-5 overflow-hidden rounded-3xl border border-[rgb(var(--brand-primary-rgb)_/_0.1)] bg-gradient-to-br from-[rgb(var(--brand-primary-rgb)_/_0.05)] via-white to-[#EFFCFD] p-5 sm:mt-8 sm:p-6 md:p-10">
        <p className="text-xs font-medium uppercase tracking-wide text-brand-primary">
          Empezá por acá
        </p>
        <p className="mt-2 max-w-lg text-lg font-semibold leading-snug text-brand-black md:text-xl">
          Conectá tu tienda y vas a ver todos tus productos acá
        </p>
        <p className="mt-1 max-w-lg text-sm text-neutral-500">
          Después podés cambiarles las fotos, el precio, el stock y la ficha sin
          salir de Atlas.
        </p>
        {/* Las mismas pills que al lado de las pestañas: conectar, y las que vienen. En el teléfono, en dos
          columnas; desde `sm`, en fila como siempre. */}
        <div className="mt-6 grid grid-cols-2 gap-2.5 sm:flex sm:flex-wrap sm:gap-3">
          {props.opciones.map((p) => (
            <PillDeConectar
              key={p}
              plataforma={p}
              conectando={props.conectando === p}
              bloqueada={!!props.conectando}
              onConectar={() => props.onConectar(p)}
              enGrilla
            />
          ))}
          {PROXIMAMENTE.map((p) => (
            <PillProximamente key={p.id} {...p} enGrilla />
          ))}
        </div>
        {props.error && (
          <p role="alert" className="mt-3 text-xs text-red-600">
            {props.error}
          </p>
        )}
      </div>

      {/* La presentación de la sección: por ahora sólo para dev testers (`useHayVideoDePresentacion`). */}
      {hayVideo && (
        <section className="mt-5 rounded-3xl border border-neutral-200 bg-surface-card p-5 shadow-sm sm:mt-6 sm:p-6 md:p-8">
          <h2 className="text-lg font-semibold leading-snug text-brand-black md:text-xl">
            Qué podés hacer con Tienda online
          </h2>
          <p className="mt-1 truncate text-sm text-neutral-500">
            Cómo editar y sincronizar tus productos.
          </p>
          <VideoDePresentacion />
        </section>
      )}
    </>
  );
}

function Modal({
  titulo,
  onCerrar,
  children,
}: {
  titulo: string;
  onCerrar: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const alApretar = (e: KeyboardEvent) => e.key === "Escape" && onCerrar();
    window.addEventListener("keydown", alApretar);
    return () => window.removeEventListener("keydown", alApretar);
  }, [onCerrar]);
  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-4"
      onClick={onCerrar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
      >
        <div className="flex items-start gap-3 border-b border-neutral-100 p-5">
          <h2 className="min-w-0 flex-1 text-base font-semibold leading-snug text-brand-black">
            {titulo}
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="-mr-2 -mt-1.5 grid h-9 w-9 shrink-0 place-items-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-brand-black"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

/** Lo que hoy se puede hacer con una publicación de Mercado Libre: verla y cambiar precio y descripción. */
function DetalleDeMercadoLibre({
  producto: p,
  varianteId,
  companyId,
  trayendo,
  error,
}: {
  producto: ProductoDeLaTienda;
  varianteId: string | null;
  companyId: string | null;
  trayendo: boolean;
  error: string | null;
}) {
  const e = ESTADO[p.estado];
  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Foto src={p.foto} className="h-24 w-24 shrink-0 rounded-xl" />
        <div className="flex min-w-0 flex-col gap-1">
          <PrecioYStock producto={p} />
          {e && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-neutral-600">
              <span
                className={`h-1.5 w-1.5 rounded-full ${e.punto}`}
                aria-hidden="true"
              />
              {e.texto}
            </span>
          )}
          {p.url && (
            <a
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:opacity-70"
            >
              Ver en Mercado Libre <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>
      {error ? (
        <p
          role="alert"
          className="rounded-xl bg-red-50 py-3 px-4 text-sm text-red-700"
        >
          {error}
        </p>
      ) : varianteId ? (
        <MercadoLibreFicha varianteId={varianteId} companyId={companyId} />
      ) : (
        <p className="flex items-center gap-2 rounded-xl bg-neutral-50 py-3 px-4 text-sm text-neutral-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          {trayendo ? "Trayendo la publicación a Atlas…" : "Cargando…"}
        </p>
      )}
      <p className="text-xs text-neutral-400">
        Por ahora, desde acá se cambian el precio y la descripción. La ficha
        completa de Mercado Libre llega pronto.
      </p>
    </div>
  );
}

/**
 * "Filtrar productos", como el panel de Tiendanube (ADR-2026-10-30-filtrar-productos): categoría, stock,
 * precio, visibilidad, envío y peso/dimensiones. Se edita una copia y se aplica con "Filtrar".
 */
function PanelDeFiltros({
  abierto,
  filtros,
  categoria,
  categorias,
  onCerrar,
  onAplicar,
}: {
  abierto: boolean;
  filtros: FiltrosDeLaTienda;
  categoria: string | null;
  categorias: CategoriaDeTienda[];
  onCerrar: () => void;
  onAplicar: (f: FiltrosDeLaTienda, categoria: string | null) => void;
}) {
  const [f, setF] = useState<FiltrosDeLaTienda>(filtros);
  const [c, setC] = useState<string | null>(categoria);
  useEffect(() => {
    if (!abierto) return;
    setF(filtros);
    setC(categoria);
  }, [abierto, filtros, categoria]);

  const grupo = <
    K extends "stock" | "precio" | "visibilidad" | "envio" | "medidas",
  >(
    titulo: string,
    clave: K,
    opciones: ReadonlyArray<{
      id: NonNullable<FiltrosDeLaTienda[K]>;
      texto: string;
    }>,
  ) => (
    <fieldset>
      <legend className="text-sm font-medium text-brand-black">{titulo}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {[{ id: undefined, texto: "Todos" }, ...opciones].map((o) => {
          const activa = f[clave] === o.id;
          return (
            <button
              key={String(o.id ?? "todos")}
              type="button"
              aria-pressed={activa}
              onClick={() => setF((x) => ({ ...x, [clave]: o.id }))}
              className={`rounded-lg border py-1.5 px-3 text-sm transition-colors ${
                activa
                  ? "border-brand-primary bg-brand-primary font-medium text-white"
                  : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {o.texto}
            </button>
          );
        })}
      </div>
    </fieldset>
  );

  const numero = (v: string) =>
    v.trim() === "" ? undefined : Math.max(0, Math.floor(Number(v)));
  const campo =
    "mt-1 w-full rounded-xl border border-neutral-200 bg-white py-2 px-3 text-sm outline-none focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.1)]";
  return (
    <SheetLateral
      open={abierto}
      onClose={onCerrar}
      titulo="Filtrar productos"
      pie={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => {
              setF({});
              setC(null);
            }}
            className={boton("neutro")}
          >
            Borrar filtros
          </button>
          <button
            type="button"
            onClick={() => onAplicar(f, c)}
            className={boton("primario")}
          >
            Filtrar
          </button>
        </div>
      }
    >
      <div className="space-y-6 p-1">
        <label className="block">
          <span className="text-sm font-medium text-brand-black">
            Categoría
          </span>
          <select
            value={c ?? ""}
            onChange={(e) => setC(e.target.value || null)}
            className={`${campo} mt-2 py-2.5`}
          >
            <option value="">Todas</option>
            <option value={SIN_CATEGORIA}>Sin categoría</option>
            {categorias.map((x) => (
              <option key={x.id} value={x.id}>
                {partesDelCamino(categorias, x.id).join(" › ")}
              </option>
            ))}
          </select>
        </label>
        <div>
          {grupo("Disponibilidad en stock", "stock", [
            { id: "en", texto: "En stock" },
            { id: "fuera", texto: "Fuera de stock" },
            { id: "cantidad", texto: "Por cantidad" },
          ])}
          {f.stock === "cantidad" && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-neutral-500">Desde</span>
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={f.stockMin ?? ""}
                  onChange={(e) =>
                    setF((x) => ({ ...x, stockMin: numero(e.target.value) }))
                  }
                  className={campo}
                />
              </label>
              <label className="block">
                <span className="text-xs text-neutral-500">Hasta</span>
                <input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={f.stockMax ?? ""}
                  onChange={(e) =>
                    setF((x) => ({ ...x, stockMax: numero(e.target.value) }))
                  }
                  className={campo}
                />
              </label>
            </div>
          )}
        </div>
        {grupo("Tipo de precio", "precio", [
          { id: "promocional", texto: "Promocional" },
          { id: "no_promocional", texto: "No promocional" },
        ])}
        {grupo("Visibilidad en la tienda", "visibilidad", [
          { id: "visible", texto: "Visibles" },
          { id: "hidden", texto: "Ocultos" },
          { id: "unlisted", texto: "No listado" },
        ])}
        {grupo("Tipo de envío", "envio", [
          { id: "gratis", texto: "Gratis" },
          { id: "pago", texto: "Pago" },
        ])}
        {grupo("Peso y dimensiones", "medidas", [
          { id: "sin_dimensiones", texto: "Sin dimensiones" },
          { id: "sin_peso", texto: "Sin peso" },
          { id: "sin_peso_ni_dimensiones", texto: "Sin peso ni dimensiones" },
        ])}
      </div>
    </SheetLateral>
  );
}

/** La casilla de una card o fila en modo selección. */
function CasillaDeSeleccion({ elegida }: { elegida: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`grid h-6 w-6 shrink-0 place-items-center rounded-lg border-2 shadow-sm transition-colors ${
        elegida
          ? "border-brand-primary bg-brand-primary text-white"
          : "border-white bg-white/90"
      }`}
    >
      {elegida && <Check className="h-4 w-4" strokeWidth={3} />}
    </span>
  );
}

/**
 * "Editar N productos" (ADR-2026-10-30-edicion-masiva): precio (fijar o porcentaje), stock, visibilidad y
 * envío gratis, y en qué tiendas. Lo que queda en "No cambiar" no se toca.
 */
function PanelDeEdicionMasiva({
  abierto,
  cuantos,
  conMl,
  onCerrar,
  onAplicar,
}: {
  abierto: boolean;
  cuantos: number;
  conMl: boolean;
  onCerrar: () => void;
  onAplicar: (e: EdicionMasiva) => void;
}) {
  const [modoPrecio, setModoPrecio] = useState<"no" | "fijar" | "porcentaje">(
    "no",
  );
  const [precio, setPrecio] = useState("");
  const [cambiarStock, setCambiarStock] = useState(false);
  const [stock, setStock] = useState("");
  const [visibilidad, setVisibilidad] = useState<Visibilidad | null>(null);
  const [envio, setEnvio] = useState<boolean | null>(null);
  const [tiendas, setTiendas] = useState({
    tiendanube: true,
    mercadolibre: true,
  });
  useEffect(() => {
    if (!abierto) return;
    setModoPrecio("no");
    setPrecio("");
    setCambiarStock(false);
    setStock("");
    setVisibilidad(null);
    setEnvio(null);
  }, [abierto]);

  const numero = (v: string) => {
    const n = Number(v.replace(",", "."));
    return v.trim() !== "" && Number.isFinite(n) ? n : null;
  };
  const edicion: EdicionMasiva = {
    ...(modoPrecio !== "no" && numero(precio) !== null
      ? { precio: { modo: modoPrecio, valor: numero(precio)! } }
      : {}),
    ...(cambiarStock && numero(stock) !== null
      ? { stock: Math.max(0, Math.floor(numero(stock)!)) }
      : {}),
    ...(visibilidad ? { visibilidad } : {}),
    ...(envio !== null ? { envioGratis: envio } : {}),
    tiendas: {
      tiendanube: tiendas.tiendanube,
      mercadolibre: conMl && tiendas.mercadolibre,
    },
  };
  const opcion = (activa: boolean) =>
    `rounded-lg border py-1.5 px-3 text-sm transition-colors ${
      activa
        ? "border-brand-primary bg-brand-primary font-medium text-white"
        : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
    }`;
  const campo =
    "mt-2 w-full rounded-xl border border-neutral-200 bg-white py-2.5 px-3 text-sm outline-none focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.1)]";
  const listo =
    hayEdicion(edicion) &&
    (edicion.tiendas.tiendanube || edicion.tiendas.mercadolibre);

  return (
    <SheetLateral
      open={abierto}
      onClose={onCerrar}
      titulo={`Editar ${cuantos} producto${cuantos === 1 ? "" : "s"}`}
      pie={
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className={boton("neutro")}>
            Cancelar
          </button>
          <button
            type="button"
            disabled={!listo}
            onClick={() => onAplicar(edicion)}
            className={boton("primario")}
          >
            Aplicar y sincronizar
          </button>
        </div>
      }
    >
      <div className="space-y-6 p-1">
        <fieldset>
          <legend className="text-sm font-medium text-brand-black">
            Precio
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {(
              [
                ["no", "No cambiar"],
                ["fijar", "Fijar precio"],
                ["porcentaje", "Subir o bajar %"],
              ] as const
            ).map(([id, texto]) => (
              <button
                key={id}
                type="button"
                aria-pressed={modoPrecio === id}
                onClick={() => setModoPrecio(id)}
                className={opcion(modoPrecio === id)}
              >
                {texto}
              </button>
            ))}
          </div>
          {modoPrecio !== "no" && (
            <>
              <input
                type="number"
                inputMode="decimal"
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                placeholder={
                  modoPrecio === "fijar"
                    ? "Ej.: 15000"
                    : "Ej.: 10 para subir, -10 para bajar"
                }
                className={campo}
              />
              {modoPrecio === "porcentaje" && (
                <p className="mt-1.5 text-xs text-neutral-500">
                  Sobre el precio que tiene hoy cada producto (y cada variante).
                </p>
              )}
            </>
          )}
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium text-brand-black">
            Stock
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              aria-pressed={!cambiarStock}
              onClick={() => setCambiarStock(false)}
              className={opcion(!cambiarStock)}
            >
              No cambiar
            </button>
            <button
              type="button"
              aria-pressed={cambiarStock}
              onClick={() => setCambiarStock(true)}
              className={opcion(cambiarStock)}
            >
              Fijar stock
            </button>
          </div>
          {cambiarStock && (
            <>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                placeholder="Ej.: 10"
                className={campo}
              />
              <p className="mt-1.5 text-xs text-neutral-500">
                Para cada variante, si el producto tiene.
              </p>
            </>
          )}
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium text-brand-black">
            Visibilidad
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              aria-pressed={visibilidad === null}
              onClick={() => setVisibilidad(null)}
              className={opcion(visibilidad === null)}
            >
              No cambiar
            </button>
            {VISIBILIDADES.map((v) => (
              <button
                key={v.id}
                type="button"
                aria-pressed={visibilidad === v.id}
                onClick={() => setVisibilidad(v.id)}
                className={opcion(visibilidad === v.id)}
              >
                {v.texto}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="text-sm font-medium text-brand-black">
            Envío gratis
          </legend>
          <div className="mt-2 flex flex-wrap gap-2">
            {(
              [
                [null, "No cambiar"],
                [true, "Con envío gratis"],
                [false, "Sin envío gratis"],
              ] as const
            ).map(([id, texto]) => (
              <button
                key={String(id)}
                type="button"
                aria-pressed={envio === id}
                onClick={() => setEnvio(id)}
                className={opcion(envio === id)}
              >
                {texto}
              </button>
            ))}
          </div>
        </fieldset>

        {conMl && (
          <fieldset>
            <legend className="text-sm font-medium text-brand-black">
              Aplicar en
            </legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {(
                [
                  ["tiendanube", "Tiendanube", true],
                  ["mercadolibre", "Mercado Libre", conMl],
                ] as const
              ).map(([id, texto, se]) => (
                <button
                  key={id}
                  type="button"
                  role="checkbox"
                  aria-checked={se && tiendas[id]}
                  disabled={!se}
                  onClick={() => setTiendas((t) => ({ ...t, [id]: !t[id] }))}
                  className={`${opcion(se && tiendas[id])} disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {texto}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-neutral-500">
              En Mercado Libre se aplica a los que ya están publicados allá
              (precio, stock y visibilidad).
            </p>
          </fieldset>
        )}
      </div>
    </SheetLateral>
  );
}

